import { AlertTriangleIcon, MailCheckIcon } from "lucide-react";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ClientInviteWithTokenResponse =
  components["schemas"]["ClientInviteWithTokenResponse"];

interface InviteRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invite: ClientInviteWithTokenResponse | null;
}

export function InviteRevealDialog({
  open,
  onOpenChange,
  invite,
}: InviteRevealDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite sent</DialogTitle>
          <DialogDescription>
            An invite email is on its way to {invite?.email}. They'll be asked to sign
            in with Google using that same address.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
          <MailCheckIcon className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <span>
            Email delivered to <strong>{invite?.email}</strong>. They can click the
            link from their inbox — no further action needed from you.
          </span>
        </div>

        <div className="space-y-1.5">
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Invite token
          </span>
          <div className="bg-muted flex items-center gap-2 rounded-md p-2">
            <code className="flex-1 break-all text-xs">{invite?.token ?? ""}</code>
            {invite?.token && <CopyButton value={invite.token} />}
          </div>
          <p className="text-muted-foreground text-xs">
            Share this token only if the email didn't arrive — append it to the
            portal's <code className="font-mono">/accept-invite?token=…</code> URL.
          </p>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
          <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            The token is shown only once — close this dialog and we can't get it back.
            To re-issue, use Resend on the invite (or invite the address again).
          </span>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
